import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { SignInButton, SignUpButton, UserButton } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useClerkSession } from '../hooks/useClerkSession';
import { clerkAppearance } from '../clerkAppearance';

const navItems = [
  { name: 'Home', href: '/' },
  { name: 'Rankings', href: '/players' },
  { name: 'Trade Calculator', href: '/trade-calculator' },
  { name: 'Draft Assistant', href: '/draft-assistant' },
  { name: 'My Roster', href: '/my-roster' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isLoaded, signedIn, signedOut, user } = useClerkSession();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          scrolled
            ? 'border-b border-white/10 bg-ink/85 shadow-lg shadow-black/20 backdrop-blur-xl'
            : 'border-b border-transparent bg-ink/70 backdrop-blur-md'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between sm:h-[4.5rem]">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2.5">
                <img
                  src="/images/Fantasy Fiesta Logo - Transparent.png"
                  alt="Fantasy Fiesta"
                  className="h-10 w-auto object-contain sm:h-11"
                />
                <span className="hidden font-display text-lg font-semibold tracking-tight text-white sm:inline">
                  Fantasy Fiesta
                </span>
              </Link>
              <div className="hidden items-center gap-1 lg:flex">
                {navItems.map((item) => {
                  const current = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                        current
                          ? 'bg-fiesta-yellow/15 text-fiesta-yellow'
                          : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="hidden items-center gap-3 lg:flex">
              {!isLoaded ? (
                <AuthPlaceholder size="md" />
              ) : signedIn ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-zinc-400">
                    Hey, {user?.firstName}
                  </span>
                  <ProfileMenu size="md" />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <SignInButton mode="modal">
                    <button className="rounded-full px-4 py-2 text-sm font-medium text-zinc-300 transition-colors hover:text-white">
                      Sign in
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button className="rounded-full bg-fiesta-yellow px-4 py-2 text-sm font-semibold text-ink transition-transform hover:scale-[1.02]">
                      Get started
                    </button>
                  </SignUpButton>
                </div>
              )}
            </div>

            {signedOut ? (
              <div className="hidden items-center gap-2 md:flex lg:hidden">
                <SignInButton mode="modal">
                  <button className="rounded-full px-4 py-2 text-sm font-medium text-zinc-300 transition-colors hover:text-white">
                    Sign in
                  </button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <button className="rounded-full bg-fiesta-yellow px-4 py-2 text-sm font-semibold text-ink">
                    Get started
                  </button>
                </SignUpButton>
              </div>
            ) : null}

            <div className="flex items-center gap-2 lg:hidden">
              {!isLoaded ? <AuthPlaceholder size="sm" /> : null}
              {signedIn ? <ProfileMenu size="sm" /> : null}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-zinc-400 hover:bg-white/5 hover:text-white"
              >
                <span className="sr-only">Open menu</span>
                {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="sticky top-16 z-40 border-b border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-1 px-4 py-4">
              {navItems.map((item) => {
                const current = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block rounded-xl px-4 py-3 font-medium ${
                      current
                        ? 'bg-fiesta-yellow/15 text-fiesta-yellow'
                        : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}

              {signedIn ? (
                <Link
                  to="/settings"
                  className={`block rounded-xl px-4 py-3 font-medium ${
                    location.pathname === '/settings'
                      ? 'bg-fiesta-yellow/15 text-fiesta-yellow'
                      : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Settings
                </Link>
              ) : null}

              {signedOut && (
                <div className="space-y-2 border-t border-white/10 pt-4">
                  <SignInButton mode="modal">
                    <button
                      className="w-full rounded-xl px-4 py-3 text-left font-medium text-zinc-300 hover:bg-white/5 hover:text-white"
                      onClick={() => setIsOpen(false)}
                    >
                      Sign in
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button
                      className="w-full rounded-xl bg-fiesta-yellow px-4 py-3 text-left font-semibold text-ink"
                      onClick={() => setIsOpen(false)}
                    >
                      Get started
                    </button>
                  </SignUpButton>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M3 5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25v1.5A2.25 2.25 0 0 1 18.75 9H5.25A2.25 2.25 0 0 1 3 6.75v-1.5Zm0 6A2.25 2.25 0 0 1 5.25 9h7.5A2.25 2.25 0 0 1 15 11.25v1.5A2.25 2.25 0 0 1 12.75 15h-7.5A2.25 2.25 0 0 1 3 12.75v-1.5Zm0 6A2.25 2.25 0 0 1 5.25 15h4.5A2.25 2.25 0 0 1 12 17.25v1.5A2.25 2.25 0 0 1 9.75 21h-4.5A2.25 2.25 0 0 1 3 18.75v-1.5Z" />
  </svg>
);

function AuthPlaceholder({ size }: { size: 'sm' | 'md' }) {
  return (
    <div className="flex items-center gap-3" aria-hidden="true">
      {size === 'md' ? (
        <span className="h-4 w-16 animate-pulse rounded bg-white/10" />
      ) : null}
      <span
        className={`animate-pulse rounded-full bg-white/10 ${
          size === 'sm' ? 'h-8 w-8' : 'h-9 w-9'
        }`}
      />
    </div>
  );
}

function ProfileMenu({ size }: { size: 'sm' | 'md' }) {
  const navigate = useNavigate();

  return (
    <UserButton
      appearance={{
        ...clerkAppearance,
        elements: {
          ...clerkAppearance.elements,
          avatarBox: size === 'sm'
            ? 'h-8 w-8 border border-[#3ee0ff]/30'
            : 'h-9 w-9 border border-[#3ee0ff]/30',
        },
      }}
    >
      <UserButton.MenuItems>
        <UserButton.Action label="manageAccount" />
        <UserButton.Action
          label="Settings"
          labelIcon={<SettingsIcon />}
          onClick={() => navigate('/settings')}
        />
        <UserButton.Action label="signOut" />
      </UserButton.MenuItems>
    </UserButton>
  );
}
